import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/k9yvnsja_IMG-20260415-WA0029.jpg";
const FLOWERING = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/2h1xn5j8_IMG-20260415-WA0041.jpg";
const WAREHOUSE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/62e8x21s_IMG-20260415-WA0015.jpg";
const FIELD_WIDE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/0t4kmt8l_IMG-20260415-WA0042.jpg";

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-20">
      {/* Header */}
      <section className="py-20 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
            Our Story
          </p>
          <h1
            data-testid="about-page-heading"
            className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-white mb-4"
          >
            About Novapine
          </h1>
          <p className="font-body text-base text-white/70 max-w-lg mx-auto">
            A family-owned agro-processing business rooted in the heart of
            Hluhluwe, KwaZulu-Natal.
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
                Since 2007
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-6">
                From Field to Finished Product
              </h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Novapine is an agro-processing company based in Hluhluwe,
                KwaZulu-Natal, South Africa. Since 2007, we have specialised in
                processing locally sourced Cayenne pineapples into high-quality
                frozen pulp, frozen chunks, dried pineapple, and whole fruit for
                commercial and industrial use.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Linked to Mkonge Farm, which cultivates all our Cayenne
                pineapples in the fertile Hluhluwe region, we maintain complete
                oversight from field to finished product. Whole fruit can also
                be supplied through Novapine, sourced directly from Mkonge
                Cayennes.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
                Our focus is on consistency, quality, and reliable supply for
                commercial and industrial clients. We are a current supplier to
                Sir Fruit and maintain a strong regional sourcing network.
              </p>
            </div>
            <div>
              <img
                src={FLOWERING}
                alt="Cayenne pineapple flowering on the plant"
                className="rounded-xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Domleo Family */}
      <section data-testid="family-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="lg:order-2">
              <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
                Farming Since 1964
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-6">
                The Domleo Family
              </h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                The Domleo family have farmed in Hluhluwe since 1964, integrating
                pineapple cultivation with an established cattle and wildlife
                operation. Built on hard work and integrity, the business
                combines traditional farming methods with the innovation of the
                next generation.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Jill and James Domleo own and lead the entire operation. They are
                the steady hand that guides the family business — from farming
                through to processing and retail. Their Hluhluwe Farmstall,
                situated on the family farm, is owned by Jill and James and run
                by Jill, who has built it into a beloved local destination that
                connects the farm directly with the community.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Their sons Lloyd and Harry manage Mkonge Farm — the growing
                side of the business, established in 1996. Lloyd oversees the
                Cayenne pineapple cultivation alongside his beloved herd of
                Nguni cattle, while Harry works closely alongside him on the
                day-to-day farming operations. Their daughter Lara-Jane handles
                all the branding across the business, bringing a creative edge
                to the family's identity.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                From this foundation, Novapine was established as the family's
                dedicated pineapple processing operation. Today, it has grown
                into a fully integrated agricultural and production enterprise,
                rooted in the Hluhluwe community and focused on quality,
                consistency, and dependable supply.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                The business operates as a fully integrated farm-to-product
                system. Mkonge Farm produces the pineapples, Novapine manages
                the processing into frozen pulp, chunks, and dried products, and
                the Hluhluwe Farmstall connects the brand directly with the
                local market. This structure allows for full control over quality
                at every stage — from cultivation through to final product.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
                With an established supply history and a hands-on approach to
                production, Novapine is positioned to support commercial
                partners and everyday consumers with consistent, high-quality
                pineapple products — while remaining deeply rooted in family
                values and community.
              </p>
            </div>
            <div className="lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/u2ecorrj_194072043_353855726072905_5478934664197181446_n.jpg"
                alt="The Domleo family at Hluhluwe Farmstall"
                className="rounded-xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Three Arms */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
              Our Operations
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown">
              Three Arms, One Family
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-[#E2E0D5] rounded-xl p-8 text-center transition-all duration-300 hover:border-novapine-gold/40 hover:shadow-md">
              <div className="w-12 h-12 rounded-xl bg-novapine-cream mx-auto flex items-center justify-center mb-4">
                <span className="font-heading font-bold text-lg text-novapine-brown">M</span>
              </div>
              <h3 className="font-heading font-semibold text-lg text-novapine-brown mb-2">
                Mkonge Farm
              </h3>
              <p className="font-body text-sm text-novapine-brown/60 leading-relaxed">
                Cultivating Cayenne pineapples and raising Nguni cattle in the
                fertile Hluhluwe region. The foundation of our supply chain.
              </p>
            </div>
            <div className="bg-white border border-[#E2E0D5] rounded-xl p-8 text-center transition-all duration-300 hover:border-novapine-gold/40 hover:shadow-md">
              <div className="w-12 h-12 rounded-xl bg-novapine-cream mx-auto flex items-center justify-center mb-4">
                <span className="font-heading font-bold text-lg text-novapine-brown">N</span>
              </div>
              <h3 className="font-heading font-semibold text-lg text-novapine-brown mb-2">
                Novapine
              </h3>
              <p className="font-body text-sm text-novapine-brown/60 leading-relaxed">
                Processing Cayenne pineapples into frozen pulp, frozen chunks,
                dried pineapple, and supplying whole fruit to commercial buyers
                across South Africa.
              </p>
            </div>
            <div className="bg-white border border-[#E2E0D5] rounded-xl p-8 text-center transition-all duration-300 hover:border-novapine-gold/40 hover:shadow-md">
              <div className="w-12 h-12 rounded-xl bg-novapine-cream mx-auto flex items-center justify-center mb-4">
                <span className="font-heading font-bold text-lg text-novapine-brown">H</span>
              </div>
              <h3 className="font-heading font-semibold text-lg text-novapine-brown mb-2">
                Hluhluwe Farmstall
              </h3>
              <p className="font-body text-sm text-novapine-brown/60 leading-relaxed">
                Connecting farm-fresh products directly with the local community
                and bringing the family's produce to the people of Hluhluwe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
              On The Ground
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown">
              Life on Mkonge Farm
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/2h1xn5j8_IMG-20260415-WA0041.jpg"
              alt="Cayenne pineapple close-up in field"
              className="rounded-xl w-full h-[250px] md:h-[300px] object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/0t4kmt8l_IMG-20260415-WA0042.jpg"
              alt="Pineapple field rows with red earth"
              className="rounded-xl w-full h-[250px] md:h-[300px] object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/0yt59mna_IMG-20260415-WA0021.jpg"
              alt="Ripe golden pineapple at sunset"
              className="rounded-xl w-full h-[250px] md:h-[300px] object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/8k9hw2e9_IMG-20260415-WA0023.jpg"
              alt="Pineapples growing in Hluhluwe landscape"
              className="rounded-xl w-full h-[250px] md:h-[300px] object-cover md:col-span-2"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/62e8x21s_IMG-20260415-WA0015.jpg"
              alt="Harvested pineapples in processing facility"
              className="rounded-xl w-full h-[250px] md:h-[300px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
                Community Impact
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-6">
                Empowering Local Communities
              </h2>
              <div className="bg-novapine-cream/60 border border-novapine-gold/20 rounded-xl p-8">
                <p className="font-body text-base md:text-lg text-novapine-brown/80 leading-relaxed">
                  Novapine is proud to support local employment in the Hluhluwe
                  region, with a processing team made up predominantly of women.
                  This forms part of our commitment to building sustainable,
                  community-driven operations.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/vwrp5qpe_68441589_400905137227341_46993981669638144_n.jpg"
                alt="Novapine processing team"
                className="rounded-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Farm Banner */}
      <section className="relative h-[300px] md:h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${FIELD_WIDE})`, backgroundAttachment: 'fixed' }}
        />
        <div className="absolute inset-0 bg-novapine-brown/50" />
        <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
          <div>
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-3">
              Mkonge Farm &mdash; Hluhluwe
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">
              From Our Fields to Your Facility
            </h2>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-6">
            Want to Work With Us?
          </h2>
          <p className="font-body text-base text-novapine-brown/60 leading-relaxed mb-10 max-w-lg mx-auto">
            Get in touch with our team to discuss supply, partnerships,
            or learn more about our operations.
          </p>
          <Link to="/contact">
            <Button
              data-testid="about-cta-button"
              className="btn-gold rounded-md px-10 py-6 font-heading text-sm tracking-wide font-semibold"
            >
              Get In Touch
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
